import type { Feature, LineString, Point } from 'geojson';

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';

export interface BBox {
  south: number;
  west: number;
  north: number;
  east: number;
}

export interface CameraProperties {
  id: number;
  ref: string | null;
  maxspeed: string | null;
}

export interface AvgLineProperties {
  ref: string | null;
  maxspeed: string | null;
}

export type CameraPoint = Feature<Point, CameraProperties>;
export type AvgLine = Feature<LineString, AvgLineProperties>;

export interface CameraSet {
  fixed: CameraPoint[];
  avgLines: AvgLine[];
  avgPoints: CameraPoint[];
}

export const EMPTY_CAMERA_SET: CameraSet = { fixed: [], avgLines: [], avgPoints: [] };

export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Snaps to a grid (0.05° ≈ 5.5 km by default) so nearby fetches share Overpass cache
// keys. Pass a finer step for small boxes — the snap grows the bbox by up to a step
// per side, which would dwarf a radius smaller than it.
export function bboxFromCenter(lat: number, lon: number, radiusKm: number, step = 0.05): BBox {
  const latDeg = radiusKm / 111;
  const lonDeg = radiusKm / (111 * Math.cos((lat * Math.PI) / 180));
  const snap = (v: number) => Math.floor(v / step) * step;
  return {
    south: snap(lat - latDeg),
    north: snap(lat + latDeg + step),
    west: snap(lon - lonDeg),
    east: snap(lon + lonDeg + step),
  };
}

interface OverpassNode {
  id: number;
  lat: number;
  lon: number;
  tags: Record<string, string | undefined>;
}

interface OverpassResponse {
  elements: OverpassNode[];
}

// Throws on fetch / parse error and on AbortError so callers can leave the previous set intact.
export async function fetchCamerasForBbox(bbox: BBox, signal?: AbortSignal): Promise<CameraSet> {
  const query = `[out:json][timeout:60];(node["highway"="speed_camera"](${bbox.south},${bbox.west},${bbox.north},${bbox.east});node["enforcement"="average_speed"](${bbox.south},${bbox.west},${bbox.north},${bbox.east}););out body;`;
  const resp = await fetch(OVERPASS_URL + '?data=' + encodeURIComponent(query), { signal });
  if (!resp.ok) throw new Error(`Overpass returned ${resp.status}`);
  const data: OverpassResponse = await resp.json();
  return splitCameras(data.elements);
}

function splitCameras(elements: OverpassNode[]): CameraSet {
  const fixed: CameraPoint[] = [];
  const avgPoints: CameraPoint[] = [];
  const avgLines: AvgLine[] = [];
  const avgNodes: CameraPoint[] = [];

  for (const el of elements) {
    const tags = el.tags ?? {};
    const isAvg = tags['speed_camera:type'] === 'average' || tags['enforcement'] === 'average_speed';
    const feature: CameraPoint = {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [el.lon, el.lat] },
      properties: { id: el.id, ref: tags.ref ?? null, maxspeed: tags.maxspeed ?? null },
    };
    if (isAvg) avgNodes.push(feature);
    else fixed.push(feature);
  }

  // Group avg nodes by ref so paired start/end nodes can be drawn as a single zone line
  const byRef: Record<string, CameraPoint[]> = {};
  for (const f of avgNodes) {
    const ref = f.properties.ref;
    if (ref) (byRef[ref] ??= []).push(f);
  }
  for (const f of avgNodes) {
    avgPoints.push(f);
    const ref = f.properties.ref;
    const group = ref ? byRef[ref] : null;
    // Emit on the first node only so each pair produces exactly one line
    if (group && group.length === 2 && group[0] === f) {
      avgLines.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [group[0].geometry.coordinates, group[1].geometry.coordinates],
        },
        properties: { ref: f.properties.ref, maxspeed: f.properties.maxspeed },
      });
    }
  }

  return { fixed, avgLines, avgPoints };
}

function dedupBy<T>(items: T[], key: (item: T) => string | number): T[] {
  const seen = new Set<string | number>();
  const out: T[] = [];
  for (const item of items) {
    const k = key(item);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
}

export function mergeCameraSets(a: CameraSet, b: CameraSet): CameraSet {
  return {
    fixed: dedupBy([...a.fixed, ...b.fixed], (f) => f.properties.id),
    avgPoints: dedupBy([...a.avgPoints, ...b.avgPoints], (f) => f.properties.id),
    avgLines: dedupBy([...a.avgLines, ...b.avgLines], (f) => f.properties.ref ?? ''),
  };
}
